import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useHistory } from "react-router-dom";
import { i18n } from "../../translate/i18n";
import { listFlowBuilders } from "../../services/flowBuilders.api";
import {
  getWhatsapp,
  listWhatsapps,
  createWhatsapp,
  updateWhatsapp,
  previewWhatsappChange,
  applyWhatsappChange,
} from "../../services/whatsapp.api";
import toastError from "../../errors/toastError";
import { DEFAULT_PROVIDER, getWhatsAppProviderLabel } from "../../constants/whatsappProviders";
import { buildWhatsAppPayload } from "./whatsAppModalHelpers";

// Estado inicial del formulario (movido del index; solo lo usa esta logica)
const initialState = {
  name: "",
  greetingMessage: "",
  complationMessage: "",
  outOfHoursMessage: "",
  ratingMessage: "",
  isDefault: false,
  token: "",
  status: "OPENING",
  provider: DEFAULT_PROVIDER,
  expiresInactiveMessage: "",
  expiresTicket: 0,
  timeUseBotQueues: 0,
  maxUseBotQueues: 3,
  timeToTransfer: ""
};

// D8.Meta.mod — Lógica (20 estados + effects + handlers + API) de WhatsAppModal.
export default function useWhatsAppModal({ open, onClose, whatsAppId, onSave, defaultProvider = DEFAULT_PROVIDER }) {
  const history = useHistory();
  const [whatsApp, setWhatsApp] = useState(initialState);
  
  // ✅ SIMPLIFICADO: Solo un departamento por conexión (cada departamento es independiente)
  const [selectedQueueId, setSelectedQueueId] = useState(''); // ✅ Departamento de la conexión (pestaña CONEXIÓN)
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState('');
  // ✅ REMOVIDO: prompts e integrations ya no se usan directamente (los componentes PromptSelect e IntegrationSelect los cargan)
  const [isLoading, setIsLoading] = useState(false);
  
  // ✅ Estados para transferencias por tiempo
  const [transferQueueId, setTransferQueueId] = useState(''); // ✅ Departamento destino para transferencias (pestaña TRANSFERENCIAS)
  const [transferWhatsappId, setTransferWhatsappId] = useState('');
  const [whatsapps, setWhatsapps] = useState([]);
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
  
  // Estados para las pestañas
  const [activeTab, setActiveTab] = useState(0);
  const [tabStates, setTabStates] = useState({
    // ✅ ELIMINADO: chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
    transfersEnabled: false,    // Por defecto DESACTIVADO ✅
    inactivityEnabled: false   // Por defecto DESACTIVADO ✅
  });

  // ✅ FASE 2: Estados para el modal de preview de cambios
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // ✅ NUEVO: Estados para modal de advertencia de conexión desconectada
  const [connectionWarningOpen, setConnectionWarningOpen] = useState(false);
  const [connectionWarningData, setConnectionWarningData] = useState(null);
  const [pendingWhatsAppData, setPendingWhatsAppData] = useState(null);

  // ✅ NUEVO: Estado para FlowBuilder (selector)
  const [selectedFlowBuilder, setSelectedFlowBuilder] = useState('');
  const [flowBuilders, setFlowBuilders] = useState([]);
  const [loadingFlowBuilders, setLoadingFlowBuilders] = useState(false);

  // ✅ MEJORAR CARGA DE DATOS
  useEffect(() => {
    let isMounted = true; // ✅ FLAG PARA VERIFICAR SI EL COMPONENTE ESTÁ MONTADO
    
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        setIsLoading(true);
        const { data } = await getWhatsapp(whatsAppId, { session: 0 });
        
        // ✅ VERIFICAR SI EL COMPONENTE SIGUE MONTADO ANTES DE ACTUALIZAR ESTADO
        if (!isMounted) return;
        
        // ✅ Asegurar que no haya valores null en los campos
        // IMPORTANTE: En JavaScript, el último objeto sobrescribe al anterior
        const sanitizedData = {
          ...initialState, // ✅ PRIMERO: Valores por defecto
          ...data, // ✅ SEGUNDO: Datos de la API (sobrescriben valores por defecto)
          // ✅ TERCERO: Asegurar valores específicos (prioridad más alta - sobrescriben todo)
          name: data.name || '',
          greetingMessage: data.greetingMessage || '',
          complationMessage: data.complationMessage || '',
          outOfHoursMessage: data.outOfHoursMessage || '',
          ratingMessage: data.ratingMessage || '',
          token: data.token !== null && data.token !== undefined ? data.token : '', // ✅ Cargar token desde la respuesta (preservar valores vacíos válidos)
          status: data.status || 'OPENING',
          provider: data.provider || DEFAULT_PROVIDER,
          // ✅ CORRECCIÓN: Manejar valores null/undefined correctamente sin usar || que puede ocultar 0 o ''
          expiresInactiveMessage: data.expiresInactiveMessage !== null && data.expiresInactiveMessage !== undefined ? data.expiresInactiveMessage : '',
          expiresTicket: data.expiresTicket !== null && data.expiresTicket !== undefined ? data.expiresTicket : 0,
          timeUseBotQueues: data.timeUseBotQueues !== null && data.timeUseBotQueues !== undefined ? data.timeUseBotQueues : 0,
          maxUseBotQueues: data.maxUseBotQueues !== null && data.maxUseBotQueues !== undefined ? data.maxUseBotQueues : 3,
          isDefault: data.isDefault !== null && data.isDefault !== undefined ? data.isDefault : false,
          timeToTransfer: data.timeToTransfer !== null && data.timeToTransfer !== undefined ? data.timeToTransfer : ''
        };
        
        // ✅ Datos cargados desde la API
        
        if (isMounted) {
          setWhatsApp(sanitizedData);
          
          // ✅ GUARDAR initialValues ESTABLES - Solo se actualiza cuando se cargan datos desde la API
          // Estos valores NO cambian cuando el usuario escribe, solo se usan para inicializar Formik
          const initialValues = {
            ...sanitizedData,
            name: sanitizedData.name || '',
            greetingMessage: sanitizedData.greetingMessage || '',
            complationMessage: sanitizedData.complationMessage || '',
            outOfHoursMessage: sanitizedData.outOfHoursMessage || '',
            ratingMessage: sanitizedData.ratingMessage || '',
            token: sanitizedData.token !== null && sanitizedData.token !== undefined ? sanitizedData.token : '',
            status: sanitizedData.status || 'OPENING',
            provider: sanitizedData.provider || DEFAULT_PROVIDER,
            // ✅ CORRECCIÓN: Manejar valores null/undefined correctamente sin usar || que puede ocultar 0 o ''
            expiresInactiveMessage: sanitizedData.expiresInactiveMessage !== null && sanitizedData.expiresInactiveMessage !== undefined ? sanitizedData.expiresInactiveMessage : '',
            expiresTicket: sanitizedData.expiresTicket !== null && sanitizedData.expiresTicket !== undefined ? sanitizedData.expiresTicket : 0,
            timeUseBotQueues: sanitizedData.timeUseBotQueues !== null && sanitizedData.timeUseBotQueues !== undefined ? sanitizedData.timeUseBotQueues : 0,
            maxUseBotQueues: sanitizedData.maxUseBotQueues !== null && sanitizedData.maxUseBotQueues !== undefined ? sanitizedData.maxUseBotQueues : 3,
            isDefault: sanitizedData.isDefault !== null && sanitizedData.isDefault !== undefined ? sanitizedData.isDefault : false,
            timeToTransfer: sanitizedData.timeToTransfer !== null && sanitizedData.timeToTransfer !== undefined ? sanitizedData.timeToTransfer : ''
          };
          setStableInitialValues(initialValues);

          // ✅ SIMPLIFICADO: Solo un departamento por conexión
          // ✅ CORRECCIÓN: Cargar el departamento de la conexión (pestaña CONEXIÓN)
          const whatsQueueId = data.queues && data.queues.length > 0 ? data.queues[0].id : '';
          setSelectedQueueId(whatsQueueId);
          
          // ✅ DETERMINAR ESTADO DE LAS PESTAÑAS BASADO EN DATOS EXISTENTES
          // ✅ ELIMINADO: chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
          const newTabStates = {
            transfersEnabled: !!(data.timeToTransfer && data.transferQueueId),
            inactivityEnabled: !!(data.expiresTicket && data.expiresInactiveMessage)
          };
          
          // ✅ CORRECCIÓN: Cargar el departamento destino para transferencias (pestaña TRANSFERENCIAS)
          // ✅ Usar una variable separada para no sobrescribir selectedQueueId
          if (data.transferQueueId !== null && data.transferQueueId !== undefined) {
            setTransferQueueId(data.transferQueueId);
          } else {
            setTransferQueueId('');
          }
          
          // ✅ Cargar transferWhatsappId si existe
          if (data.transferWhatsappId !== null && data.transferWhatsappId !== undefined) {
            setTransferWhatsappId(data.transferWhatsappId);
          } else {
            setTransferWhatsappId('');
          }
          
          // ✅ CARGAR CORRECTAMENTE EL PROMPT ID
          // ✅ CORRECCIÓN v1.3.0: Normalizar promptId para evitar valores null
          if (data.promptId !== null && data.promptId !== undefined) {
            setSelectedPrompt(data.promptId);
          }
          
          // ✅ CARGAR CORRECTAMENTE EL FLOWBUILDER ID
          if (data.flowBuilderId !== null && data.flowBuilderId !== undefined) {
            setSelectedFlowBuilder(data.flowBuilderId);
          } else {
            // FIX: limpiar el FLOWBUILDER, no el prompt. El bug previo (setSelectedPrompt(''))
            // borraba el prompt recién cargado en toda conexión sin flowbuilder → el modal
            // mostraba el prompt vacío aunque estuviera configurado (y al Guardar lo borraba).
            setSelectedFlowBuilder('');
          }
          
          // ✅ CARGAR CORRECTAMENTE EL INTEGRATION ID
          // ✅ CORRECCIÓN v1.3.0: Normalizar integrationId para evitar valores null
          if (data.integrationId !== null && data.integrationId !== undefined) {
            setSelectedIntegration(data.integrationId);
          } else {
            setSelectedIntegration('');
          }
          
          setTabStates(newTabStates);
        }

      } catch (err) {
        if (isMounted) {
          console.error("❌ ERROR AL CARGAR DATOS:", err);
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchSession();
    
    // ✅ CLEANUP: Marcar como desmontado cuando el componente se desmonte
    return () => {
      isMounted = false;
    };
  }, [whatsAppId]);

  // ✅ REMOVIDO: Los useEffect para cargar prompts e integrations ya no son necesarios
  // Los componentes PromptSelect e IntegrationSelect cargan sus propios datos

  // ✅ Cargar conexiones WhatsApp para transferencias por tiempo
  const { user } = useContext(AuthContext);
  const { companyId } = user || {};
  
  useEffect(() => {
    const loadWhatsapps = async () => {
      if (!companyId) return;
      
      setLoadingWhatsapps(true);
      try {
        const { data } = await listWhatsapps({ companyId, session: 0 });
        setWhatsapps(data || []);
      } catch (err) {
        console.error("Error cargando conexiones WhatsApp:", err);
        toastError(err);
      } finally {
        setLoadingWhatsapps(false);
      }
    };

    loadWhatsapps();
  }, [companyId]);

  // ✅ MANEJAR CAMBIO DE PESTAÑA
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ✅ MANEJAR CAMBIO DE ESTADO DE CHECKBOXES
  const handleCheckboxChange = (field, checked, setFieldValue) => {
    setTabStates(prev => ({
      ...prev,
      [field]: checked
    }));

    // ✅ LIMPIAR CAMPOS Y ESTADOS RELACIONADOS AL DESACTIVAR
    if (field === 'transfersEnabled' && !checked) {
      // ✅ CORRECCIÓN: Limpiar campo de tiempo, departamento destino Y conexión WhatsApp cuando se desactiva
      setFieldValue('timeToTransfer', '');
      setTransferQueueId(''); // ✅ CORRECCIÓN: Limpiar transferQueueId, NO selectedQueueId (que es el departamento de la conexión)
      setTransferWhatsappId(''); // ✅ CORRECCIÓN: Limpiar también transferWhatsappId
    }
    
    if (field === 'inactivityEnabled' && !checked) {
      // ✅ CORRECCIÓN: Limpiar campos de inactividad cuando se desactiva (usar 0 para expiresTicket, '' para message)
      setFieldValue('expiresTicket', 0);
      setFieldValue('expiresInactiveMessage', '');
    }
    
    // ✅ ELIMINADO: Lógica de chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
  };

  const handleClose = useCallback(() => {
    onClose();
    setWhatsApp(initialState);
    setStableInitialValues(null); // ✅ LIMPIAR initialValues ESTABLES
    setSelectedQueueId(''); // ✅ LIMPIAR departamento de la conexión
    setTransferQueueId(''); // ✅ LIMPIAR departamento destino de transferencias
    setTransferWhatsappId(''); // ✅ CORRECCIÓN: Limpiar también transferWhatsappId
    setSelectedPrompt('');
    setSelectedIntegration('');
    setSelectedFlowBuilder('');
    setIsLoading(false);
    setActiveTab(0);
    setTabStates({
      // ✅ ELIMINADO: chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
      transfersEnabled: false,
      inactivityEnabled: false
    });
  }, [onClose]);

  // ✅ NUEVO: Cargar lista de flujos FlowBuilder cuando se abre el modal
  useEffect(() => {
    if (!open) return;
    
    const loadFlowBuilders = async () => {
      try {
        setLoadingFlowBuilders(true);
        const { data } = await listFlowBuilders({ active: true });
        
        setFlowBuilders(data?.records || []);
      } catch (err) {
        console.error("Error cargando flujos FlowBuilder:", err);
        setFlowBuilders([]);
      } finally {
        setLoadingFlowBuilders(false);
      }
    };
    
    loadFlowBuilders();
  }, [open]);

  const handleSaveWhatsApp = useCallback(async (values) => {
    try {
      setIsLoading(true);

      // ✅ VALIDACIÓN: Verificar estado de conexión WhatsApp antes de guardar (solo si hay transferWhatsappId)
      if (transferWhatsappId && transferWhatsappId !== "" && transferWhatsappId !== 0) {
        const selectedWhatsappObj = whatsapps.find(w => w.id === Number(transferWhatsappId));
        if (selectedWhatsappObj && selectedWhatsappObj.status !== 'CONNECTED') {
          const whatsappName = selectedWhatsappObj.name || `Conexión ${selectedWhatsappObj.id}`;
          
          // ✅ MOSTRAR MODAL DE ADVERTENCIA en lugar de toast rápido
          setConnectionWarningData({
            connectionName: whatsappName,
            connectionStatus: selectedWhatsappObj.status
          });
          setConnectionWarningOpen(true);
          setIsLoading(false);
          return;
        }
      }
      
      // ✅ VALIDACIÓN MEJORADA: Solo requerir nombre y estatus para conexión básica
      let currentQueueId = selectedQueueId;
      let currentPrompt = selectedPrompt;
      let currentIntegration = selectedIntegration;
      
      // ✅ SIMPLIFICADO: Validación mutuamente exclusiva para DEPARTAMENTO | PROMPT | INTEGRACIÓN | FLOWBUILDER
      // ✅ Solo un departamento por conexión (cada departamento es independiente)
      const hasDepartment = currentQueueId && currentQueueId !== null && currentQueueId !== "" && currentQueueId !== 0;
      const hasPrompt = currentPrompt && currentPrompt !== null && currentPrompt !== "";
      const hasIntegration = currentIntegration && currentIntegration !== null && currentIntegration !== "";
      const hasFlowBuilder = selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "";
      
      // ✅ Contar cuántas opciones están activas
      const activeCount = [hasDepartment, hasPrompt, hasIntegration, hasFlowBuilder].filter(Boolean).length;
      
      // ✅ VALIDACIÓN: Solo UNA opción puede estar activa a la vez
      if (activeCount > 1) {
        toast.error("❌ Error: Solo puedes seleccionar UNA opción: Departamento, Prompt, Integración o FlowBuilder. No puedes seleccionar múltiples opciones simultáneamente.");
        setIsLoading(false);
        return;
      }
      
      // ✅ FORZAR LIMPIEZA SEGÚN LA OPCIÓN SELECCIONADA
      if (hasDepartment) {
        currentPrompt = '';
        currentIntegration = '';
        setSelectedFlowBuilder('');
      } else if (hasPrompt) {
        currentQueueId = '';
        currentIntegration = '';
        setSelectedFlowBuilder('');
      } else if (hasIntegration) {
        currentQueueId = '';
        currentPrompt = '';
        setSelectedFlowBuilder('');
      } else if (hasFlowBuilder) {
        currentQueueId = '';
        currentPrompt = '';
        currentIntegration = '';
        setSelectedPrompt('');
        setSelectedIntegration('');
      }
      
      // ✅ ELIMINAR VALIDACIÓN OBLIGATORIA DE DEPARTAMENTO/PROMPT
      // Una conexión puede existir solo con nombre y estatus
      // if (!hasDepartment && !hasPrompt) {
      //   toast.error("❌ Error: Debes seleccionar un departamento O un prompt para continuar.");
      //   setIsLoading(false);
      //   return;
      // }

      // ✅ ELIMINADO: Validación de chatbot - Ya no se usa (pestaña CHATBOT eliminada)
      // Los departamentos ahora trabajan de forma inteligente, no necesitan chatbot inicial

      // ✅ VALIDAR CAMPOS OBLIGATORIOS SEGÚN ESTADO DE CHECKBOXES
      // IMPORTANTE: Solo validar si las transferencias están ACTIVADAS
      if (tabStates.transfersEnabled) {
        // Verificar que ambos campos tengan contenido
        const hasTimeToTransfer = values.timeToTransfer && (typeof values.timeToTransfer === 'number' || (typeof values.timeToTransfer === 'string' && values.timeToTransfer.trim().length > 0));
        const hasTransferQueueId = transferQueueId && transferQueueId !== '';
        
        if (!hasTimeToTransfer || !hasTransferQueueId) {
          toast.error("❌ Error: Si activas Transferencias, debes configurar tiempo y departamento destino.");
          setIsLoading(false);
          return;
        }
      }
      // ✅ Si las transferencias están DESACTIVADAS, NO validar nada (los campos no se muestran)

      // ✅ VALIDAR CAMPOS OBLIGATORIOS SEGÚN ESTADO DE CHECKBOXES
      // IMPORTANTE: Solo validar si la inactividad está ACTIVADA
      if (tabStates.inactivityEnabled) {
        // Verificar que ambos campos tengan contenido
        const hasExpiresTicket = values.expiresTicket && (typeof values.expiresTicket === 'number' || (typeof values.expiresTicket === 'string' && values.expiresTicket.trim().length > 0));
        const hasExpiresMessage = values.expiresInactiveMessage && typeof values.expiresInactiveMessage === 'string' && values.expiresInactiveMessage.trim().length > 0;
        
        if (!hasExpiresTicket || !hasExpiresMessage) {
          toast.error("❌ Error: Si activas Cierre por Inactividad, debes configurar tiempo y mensaje.");
          setIsLoading(false);
          return;
        }
      }
      // ✅ Si la inactividad está DESACTIVADA, NO validar nada (los campos no se muestran)
      
      // ✅ PREPARAR DATOS CORRECTAMENTE
      // ✅ SIMPLIFICADO: Solo un departamento por conexión
      const whatsappData = buildWhatsAppPayload(values, {
        hasDepartment,
        currentQueueId,
        hasPrompt,
        currentPrompt,
        selectedIntegration,
        selectedFlowBuilder,
        tabStates,
        transferQueueId,
        transferWhatsappId,
      });
      // "session" (blob de Baileys) nunca se carga en el formulario (?session=0 en el GET),
      // por lo que no forma parte de `values`. El delete fue una guardia defensiva innecesaria.

      // ✅ FASE 2: Si estamos editando, verificar si hay cambios que afecten tickets
      if (whatsAppId) {
        try {
          const { data: preview } = await previewWhatsappChange(whatsAppId, {
            queueIds: whatsappData.queueIds,
            promptId: whatsappData.promptId,
            integrationId: whatsappData.integrationId,
            flowBuilderId: whatsappData.flowBuilderId
          });

          // Si hay cambios que requieren confirmación, mostrar modal
          if (preview.requiresConfirmation) {
            setPendingWhatsAppData(whatsappData);
            setPreviewData(preview);
            setPreviewModalOpen(true);
            setIsLoading(false);
            return; // Detener aquí, el guardado se hará después de confirmar
          }
        } catch (error) {
          console.error("❌ Error al obtener preview:", error);
          const errorMessage = error?.response?.data?.details || error?.response?.data?.error || "Error al validar cambios";
          toast.error(`⚠️ Error al validar cambios: ${errorMessage}. Se guardará sin validación.`);
          // Si falla el preview, continuar con el guardado normal pero informar al usuario
        }
      }

      // ✅ Guardar normalmente si no hay preview o no requiere confirmación
      if (whatsAppId) {
        await updateWhatsapp(whatsAppId, whatsappData);
      } else {
        await createWhatsapp(whatsappData);
      }
      
      toast.success(i18n.t("whatsappModal.success"));
      
      if (onSave) {
        onSave();
      }
      
      handleClose();
    } catch (err) {
      console.error("❌ ERROR AL GUARDAR:", err);
      toastError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedQueueId, 
    selectedPrompt, 
    selectedIntegration,
    transferQueueId, // ✅ Agregar transferQueueId a las dependencias
    tabStates, 
    whatsAppId,
    transferWhatsappId,
    whatsapps, // ✅ Agregar whatsapps a las dependencias (usado en validación)
    onSave, 
    handleClose
  ]);

  // ✅ FASE 2: Función para confirmar y aplicar cambios después del preview
  const handleConfirmConfigChange = useCallback(async () => {
    try {
      if (!pendingWhatsAppData || !whatsAppId) {
        throw new Error("No hay datos pendientes para guardar");
      }

      // Aplicar cambios a tickets afectados
      await applyWhatsappChange(whatsAppId, {
        changeType: previewData.changeType,
        newConfig: {
          promptId: pendingWhatsAppData.promptId,
          integrationId: pendingWhatsAppData.integrationId,
          flowBuilderId: pendingWhatsAppData.flowBuilderId
        }
      });

      // Guardar configuración del WhatsApp
      await updateWhatsapp(whatsAppId, pendingWhatsAppData);
      
      toast.success(i18n.t("whatsappModal.success"));
      
      if (onSave) {
        onSave();
      }
      
      // Cerrar modal de preview
      setPreviewModalOpen(false);
      setPendingWhatsAppData(null);
      setPreviewData(null);
      
      // Cerrar modal principal
      handleClose();

      return { success: true, updatedTickets: previewData.affectedTickets, errors: 0 };
    } catch (error) {
      console.error("❌ Error al aplicar cambios:", error);
      throw error;
    }
  }, [pendingWhatsAppData, whatsAppId, previewData, onSave, handleClose]);

  // ✅ SIMPLIFICADO: Manejar cambio de departamento (solo uno)
  const handleChangeQueue = (selectedId) => {
    if (selectedId && selectedId !== null && selectedId !== "" && selectedId !== 0) {
      setSelectedPrompt('');
      setSelectedIntegration('');
      setSelectedQueueId(selectedId);
    } else {
      setSelectedPrompt('');
      setSelectedIntegration('');
      setSelectedQueueId('');
    }
  };

  // ✅ SIMPLIFICADO: Al seleccionar PROMPT, limpiar DEPARTAMENTO e INTEGRACIÓN
  const handleChangePrompt = (e) => {
    const value = e.target ? e.target.value : e;
    if (value && value !== null && value !== "") {
      setSelectedQueueId('');
      setSelectedIntegration('');
      setSelectedPrompt(value);
    } else if (!value || value === null || value === "") {
      setSelectedQueueId('');
      setSelectedIntegration('');
      setSelectedPrompt('');
    }
  };

  // ✅ SIMPLIFICADO: Al seleccionar INTEGRACIÓN, limpiar DEPARTAMENTO, PROMPT y FLOWBUILDER
  const handleChangeIntegration = (e) => {
    const value = e.target ? e.target.value : e;
    if (value && value !== null && value !== "") {
      setSelectedQueueId('');
      setSelectedPrompt('');
      setSelectedFlowBuilder('');
      setSelectedIntegration(value);
    } else if (!value || value === null || value === "") {
      setSelectedQueueId('');
      setSelectedPrompt('');
      setSelectedFlowBuilder('');
      setSelectedIntegration('');
    }
  };

  // ✅ ESTADO PARA ALMACENAR initialValues - Solo se actualiza cuando se cargan datos desde la API
  const [stableInitialValues, setStableInitialValues] = useState(null);

  // ✅ CALCULAR initialValues - Solo cambiar cuando se cargan datos desde la API
  // IMPORTANTE: Este useMemo solo recalcula cuando stableInitialValues cambia (datos desde API)
  // NO recalcula cuando el usuario escribe o cambia de pestaña, manteniendo los valores
  const formikInitialValues = useMemo(() => {
    // Si hay valores estables guardados, usar esos DIRECTAMENTE (misma referencia)
    // Esto evita que Formik se resetee cuando el usuario escribe o cambia de pestaña
    if (stableInitialValues) {
      return stableInitialValues;
    }
    
    // Si no hay valores estables, usar whatsApp (para crear nuevo WhatsApp)
    return {
      ...whatsApp,
      name: whatsApp.name || '',
      greetingMessage: whatsApp.greetingMessage || '',
      complationMessage: whatsApp.complationMessage || '',
      outOfHoursMessage: whatsApp.outOfHoursMessage || '',
      ratingMessage: whatsApp.ratingMessage || '',
      token: whatsApp.token !== null && whatsApp.token !== undefined ? whatsApp.token : '',
      status: whatsApp.status || 'OPENING',
      provider: defaultProvider || whatsApp.provider || DEFAULT_PROVIDER,
      expiresInactiveMessage: whatsApp.expiresInactiveMessage || '',
      expiresTicket: whatsApp.expiresTicket || 0,
      timeUseBotQueues: whatsApp.timeUseBotQueues || 0,
      maxUseBotQueues: whatsApp.maxUseBotQueues || 3,
      isDefault: whatsApp.isDefault || false,
      timeToTransfer: whatsApp.timeToTransfer || ''
    };
  }, [stableInitialValues, whatsApp, defaultProvider]);

  return {
    activeTab,
    companyId,
    connectionWarningData,
    connectionWarningOpen,
    flowBuilders,
    formikInitialValues,
    handleChangeIntegration,
    handleChangePrompt,
    handleChangeQueue,
    handleCheckboxChange,
    handleClose,
    handleConfirmConfigChange,
    handleSaveWhatsApp,
    handleTabChange,
    history,
    isLoading,
    loadingFlowBuilders,
    loadingWhatsapps,
    pendingWhatsAppData,
    previewData,
    previewModalOpen,
    selectedFlowBuilder,
    selectedIntegration,
    selectedPrompt,
    selectedQueueId,
    setActiveTab,
    setConnectionWarningData,
    setConnectionWarningOpen,
    setFlowBuilders,
    setIsLoading,
    setLoadingFlowBuilders,
    setLoadingWhatsapps,
    setPendingWhatsAppData,
    setPreviewData,
    setPreviewModalOpen,
    setSelectedFlowBuilder,
    setSelectedIntegration,
    setSelectedPrompt,
    setSelectedQueueId,
    setStableInitialValues,
    setTabStates,
    setTransferQueueId,
    setTransferWhatsappId,
    setWhatsApp,
    setWhatsapps,
    stableInitialValues,
    tabStates,
    transferQueueId,
    transferWhatsappId,
    user,
    whatsApp,
    whatsapps,
  };
}
